import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'

import { SegmentedToggle } from '../../design-system/SegmentedToggle'
import { DateEditor } from '../inline-edit/editors'
import type { DateEditorPopup } from '../inline-edit/editors'
import { HolidayLength, HolidayPatch, HolidayRow, datePickable, holidayLength, patchIsComplete } from './logic'

const PANEL_CLASS = 'vt-holiday-dates-panel'

// Always under the row, from its left edge, at once: a panel flipping above the row or sliding in
// reads as a different control. In the body, so the grid's scroll box does not clip it.
const PANEL_POPUP: DateEditorPopup = {
  showNow: false,
  placement: 'bottomLeft',
  getPopupContainer: () => document.body,
  transitionName: '',
}

// The input reads like the cell it opens from; the other two are what typing may parse.
const EDITOR_DATE_FORMAT = ['dddd, MMMM D', 'D MMM YYYY', 'YYYY-MM-DD']

// The picker gives its extra footer no padding, and the footer is wider than one calendar, so the
// calendar is centred in the panel rather than left in its corner. Rendered by the tab: a style
// element inside the cell would count as the cell's text.
//
// On a phone the panel is kept inside the screen, and a range shows one calendar instead of two side by
// side. Its next-month arrows are shown again: the picker hides them on the first of two calendars.
export const datesPanelStyles = `
.${PANEL_CLASS} .ant-picker-footer-extra { padding: ${spacing.xs}px ${spacing.sm}px ${spacing.sm}px; }
.${PANEL_CLASS} .ant-picker-panel-layout > div { display: flex; flex-direction: column; align-items: center; }
@media (max-width: 575px) {
  .${PANEL_CLASS} { left: ${spacing.xs}px !important; max-width: calc(100vw - ${spacing.xs * 2}px); }
  .${PANEL_CLASS} .ant-picker-range-arrow { display: none; }
  .${PANEL_CLASS} .ant-picker-panels > .ant-picker-panel:not(:first-child) { display: none; }
  .${PANEL_CLASS} .ant-picker-panels > .ant-picker-panel:first-child .ant-picker-header-next-btn,
  .${PANEL_CLASS} .ant-picker-panels > .ant-picker-panel:first-child .ant-picker-header-super-next-btn { visibility: visible !important; }
}
`

export interface HolidayDatesEditorProps {
  row: HolidayRow
  /** Half day only when the location's working day has a known length. */
  allowHalfDay: boolean
  weekStart: number
  today?: dayjs.Dayjs
  commit: (patch: HolidayPatch) => void
  cancel: () => void
}

export function HolidayDatesEditor({ row, allowHalfDay, weekStart, today, commit, cancel }: HolidayDatesEditorProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const [length, setLength] = useState<HolidayLength>(holidayLength(row))
  // A pick, Enter and the picker closing can all land for one edit; only the first one counts.
  const settled = useRef(false)

  const options = [
    { value: 'SINGLE_DAY', label: formatMessage({ id: 'components.createHolidaysForm.singleDayHoliday' }) },
    { value: 'MULTI_DAY', label: formatMessage({ id: 'components.createHolidaysForm.multiDayHoliday' }) },
    ...(allowHalfDay ? [{ value: 'HALF_DAY', label: formatMessage({ id: 'components.createHolidaysForm.halfDayHoliday' }) }] : []),
  ]

  const finish = (patch?: HolidayPatch) => {
    if (settled.current) return
    settled.current = true
    if (patch) {
      commit(patch)
    } else {
      cancel()
    }
  }

  // Typed text can name a day the calendar would not offer.
  const send = (patch: HolidayPatch) => {
    const inYear = [patch.startDate, patch.endDate].every((date) => !date || datePickable(row, dayjs(date), today))
    if (inYear && patchIsComplete(row, patch)) {
      finish(patch)
    }
  }

  // Leaving with only the length changed keeps the current dates; a span still needs its range.
  const leave = () => {
    const patch = { length }
    finish(length !== holidayLength(row) && patchIsComplete(row, patch) ? patch : undefined)
  }

  const footer = (
    <SegmentedToggle
      label={formatMessage({ id: 'components.createHolidaysForm.holidayType' })}
      options={options}
      value={length}
      onChange={(next) => setLength(next as HolidayLength)}
      fitNarrowScreens
    />
  )

  return (
    <span
      style={{ display: 'contents' }}
      // Capture: the range picker cancels on Escape itself, which would otherwise save the length.
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') {
          finish()
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && length !== 'MULTI_DAY') {
          leave()
        }
      }}
    >
      <DateEditor
        value={holidayDay(row.date)}
        endValue={row.endDate && holidayDay(row.endDate)}
        range={length === 'MULTI_DAY'}
        format={EDITOR_DATE_FORMAT}
        footer={footer}
        popupClassName={PANEL_CLASS}
        popup={PANEL_POPUP}
        weekStart={weekStart}
        disabledDate={(day) => !datePickable(row, day, today)}
        commit={(start, end) => {
          // A span whose ends are the same day is a single day, whatever the footer said.
          if (end && end !== start) {
            send({ length: 'MULTI_DAY', startDate: start, endDate: end })
          } else {
            send({ length: length === 'MULTI_DAY' ? 'SINGLE_DAY' : length, startDate: start })
          }
        }}
        cancel={leave}
      />
    </span>
  )
}
